import yaml from "js-yaml";

export interface SurveyYamlDetails {
  title?: string;
  description?: string;
}

const trimmed = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

/**
 * Reads the title and description out of a survey YAML, client-side.
 *
 * Two shapes are in use. Most files nest the metadata under `details:`, but some put
 * `title` and `description` at the top level — of the survey fixtures in rnd-llm-survey,
 * thirteen are nested and three are flat. Reading only the nested one meant the import
 * silently did nothing for a flat file: no fields filled, no message, and the Create
 * button stayed disabled because it requires a name. The researcher was left typing what
 * the checkbox had just promised to fill in.
 *
 * Nested wins when both are present: it is the newer shape, and a file carrying both is
 * likelier to have the accurate value there.
 *
 * Still returns an empty object for an unparseable file. Importing is a convenience, and
 * the API is what validates the YAML.
 */
export const readSurveyYamlDetails = async (file: File): Promise<SurveyYamlDetails> => {
  try {
    const parsed = yaml.load(await file.text()) as Record<string, unknown> | null;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }

    const details =
      parsed.details && typeof parsed.details === "object" && !Array.isArray(parsed.details)
        ? (parsed.details as Record<string, unknown>)
        : {};

    return {
      title: trimmed(details.title) ?? trimmed(parsed.title),
      description: trimmed(details.description) ?? trimmed(parsed.description),
    };
  } catch (err) {
    console.error("Error parsing survey YAML for import:", err);
    return {};
  }
};
